import React, { useState, useRef, useCallback } from 'react';
import DashedBorder from '../../../components/utils/CustomDashBorder';
import { ProgressBar } from '../ProgressBar/ProgressBar.jsx';
import UploadPrompt from './UploadPrompt.jsx';
import UploadedFileItem from './UploadedFileItem.jsx';
import CustomUploader from '../../../components/upload/CustomUploader';
import { useToast } from '../Toast/ToastContext.jsx';
import { formatFileSize } from '../../../components/utils/FileUtils.js';

/**
 * FileUploader — Drag-and-drop file upload component.
 *
 * Three visual states:
 * 1. Empty / prompt — shows the UploadPrompt (drag/click to upload)
 * 2. Uploading — shows ProgressBar with percentage
 * 3. Uploaded — shows UploadedFileItem with file name, size, and remove button
 *
 * Drag handling uses a counter-based approach for dragEnter/dragLeave
 * to handle nested child elements correctly (each child fires its own
 * drag events, but we only care about the outermost boundary).
 *
 * @param {Object}   currentFile       - File state: { file, name, extension, size, uploadedUrl, uploaded }
 * @param {func}     setCurrentFile    - State setter for currentFile
 * @param {number}   uploadProgress    - 0–100 progress value
 * @param {func}     setUploadProgress - Progress setter
 * @param {boolean}  fileUploading     - Whether an upload is currently in progress
 * @param {func}     onUploadBegin     - Callback: upload started
 * @param {func}     onUploadSuccess   - Callback: upload succeeded (receives uploaded file)
 * @param {func}     onUploadError     - Callback: upload failed (receives error message)
 * @param {string[]} validFileTypes    - Allowed file extensions (e.g. ['csv', 'xlsx'])
 * @param {number}   totalProgress     - External progress override (for multi-step uploads)
 */
export const FileUploader = ({
    currentFile,
    setCurrentFile,
    uploadProgress,
    setUploadProgress,
    fileUploading,
    onUploadBegin,
    onUploadSuccess,
    onUploadError,
    validFileTypes = ["csv", "xls", "xlsx"],
    secondaryText,
    showBorder = true,
    rootStyle = {},
    dropzoneStyle = {},
    promptStyle = {},
    promptClass = "",
    progressStyle = {},
    itemStyle = {},
    borderStyle = {},
    totalProgress,
}) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);
    const toast = useToast();
    const dragCounter = useRef(0); // Tracks nested dragEnter/Leave events

    // ─── Drag handlers ──────────────────────────────────────────────
    // dragCounter ensures we only toggle the highlight when the cursor
    // enters/leaves the outermost boundary, not child elements.

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        if (dragCounter.current > 0) {
            setIsDragOver(true);
        }
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current === 0) {
            setIsDragOver(false);
        }
    }, []);

    /** Handle file drop — validates extension, then sets the file in state */
    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current = 0;
        setIsDragOver(false);
        if (fileUploading) return;

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0]; // Single file upload only
            const ext = file.name.split('.').pop().toLowerCase();
            
            // Validate file extension
            if (validFileTypes && !validFileTypes.includes(ext)) {
                const err = "Unsupported file format. Please upload a .csv or Excel file.";
                toast.warning(err);
                onUploadError?.(err);
                return;
            }

            setCurrentFile({
                file,
                name: file.name,
                extension: ext,
                size: formatFileSize(file.size),
                uploadedUrl: null,
                uploaded: false
            });
        }
    }, [validFileTypes, setCurrentFile, onUploadError, fileUploading]);

    // ─── Upload lifecycle callbacks ─────────────────────────────────

    const onUploadBeginCallback = useCallback(() => {
        onUploadBegin?.();
    }, [onUploadBegin]);

    const onUploadFinishCallback = useCallback((uploadedFile) => {
        onUploadSuccess?.(uploadedFile);
    }, [onUploadSuccess]);

    const onUploadErrorCallback = useCallback((msg) => {
        const err = msg || "Something went wrong while uploading the file. Please try again.";
        toast.warning(err);
        onUploadError?.(err);
    }, [onUploadError, toast]);

    /** Remove the uploaded file and reset state */
    const removeFile = useCallback(() => {
        setCurrentFile({
            file: null,
            name: null,
            uploaded: false,
            uploadedUrl: null,
        });
    }, [setCurrentFile]);

    return (
        <div className="file-uploader-root" style={rootStyle}>
            {/* State 1 & 2: Show dropzone when no file is uploaded, or during upload */}
            {(!currentFile.uploaded || fileUploading || totalProgress !== undefined) && (
                <div
                    className={`file-uploader-dropzone ${isDragOver ? 'is-drag-over' : ''} ${!fileUploading ? 'is-clickable' : ''}`}
                    onDragEnter={handleDragEnter}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => !fileUploading && fileInputRef.current?.click()}
                    style={{
                        minHeight: 160,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        ...dropzoneStyle,
                    }}
                >
                    {/* Dashed border overlay — color changes on drag hover */}
                    {showBorder && (
                        <DashedBorder
                            dashLength={borderStyle.dashLength || 7}
                            strokeWidth={borderStyle.strokeWidth || 1}
                            borderRadius={borderStyle.borderRadius || 4}
                            dashOffset={borderStyle.dashOffset || 5}
                            color={isDragOver ? " color-mix(in srgb, var(--blue-500) 20%, transparent)" : (borderStyle.color || "var(--color-border-default)")}
                        />
                    )}

                    {/* Upload prompt — "Choose a file or drag and drop" */}
                    {!fileUploading && (!currentFile.uploaded || totalProgress !== undefined) && (
                        <UploadPrompt
                            onSelect={() => fileInputRef.current?.click()}
                            hint={secondaryText}
                            customStyle={promptStyle}
                            customClass={promptClass}
                        />
                    )}

                    {/* Progress bar — shown during active upload */}
                    {fileUploading && (
                        <ProgressBar
                            progress={totalProgress !== undefined ? totalProgress : uploadProgress}
                            fileName={currentFile.name}
                            customStyle={{ width: '75%', maxWidth: 640, ...progressStyle }}
                        />
                    )}

                    {/* Hidden file input — programmatically triggered by click/prompt */}
                    <CustomUploader
                        currentFile={currentFile}
                        setCurrentFile={setCurrentFile}
                        isRefedTo={fileInputRef}
                        onUploadBeginCallback={onUploadBeginCallback}
                        onUploadFinishCallback={onUploadFinishCallback}
                        onUploadErrorCallback={onUploadErrorCallback}
                        setProgress={setUploadProgress}
                        uploadFinishDelay={800}
                        validFileTypes={validFileTypes}
                    />

                </div>
            )}

            {/* State 3: Show uploaded file details once upload completes */}
            {currentFile.uploaded && !fileUploading && totalProgress === undefined && (
                <UploadedFileItem
                    fileName={currentFile.name}
                    fileSize={currentFile.size}
                    onRemove={removeFile}
                    customStyle={itemStyle}
                />
            )}
        </div>
    );
};

export default FileUploader;
